import React, { useEffect, useState, useCallback } from "react";
import { HardDrive, Download, Upload, Trash2, Loader2, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { Section, Skeleton, EmptyState } from "../components/UI";

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function BackupDatabasePage() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [message, setMessage] = useState(null); // { type: "success"|"error", text }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backups");
      setBackups(data);
    } catch (e) {
      console.error("Failed to load backups:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const createBackup = async () => {
    setCreating(true);
    setMessage(null);
    try {
      const { data } = await api.post("/admin/backup");
      showMessage("success", `Backup "${data.filename}" berhasil dibuat (${formatSize(data.size)})`);
      await load();
    } catch (e) {
      showMessage("error", formatApiError(e.response?.data?.detail) || e.message);
    }
    setCreating(false);
  };

  const downloadBackup = async (filename) => {
    try {
      const response = await api.get(`/admin/backups/${filename}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showMessage("error", "Gagal mengunduh backup");
    }
  };

  const restoreBackup = async (filename) => {
    if (!window.confirm(
      `⚠️ PERINGATAN!\n\nAnda akan me-restore database dari backup "${filename}".\n\nSemua data saat ini akan DITIMPA dengan data dari backup ini.\n\nApakah Anda yakin ingin melanjutkan?`
    )) return;

    setRestoring(filename);
    setMessage(null);
    try {
      await api.post(`/admin/restore/${filename}`);
      showMessage("success", `Database berhasil di-restore dari "${filename}"`);
    } catch (e) {
      showMessage("error", formatApiError(e.response?.data?.detail) || e.message);
    }
    setRestoring(null);
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Hapus backup "${filename}"?\n\nFile ini tidak dapat dikembalikan.`)) return;
    try {
      await api.delete(`/admin/backups/${filename}`);
      showMessage("success", `Backup "${filename}" berhasil dihapus`);
      await load();
    } catch (e) {
      showMessage("error", formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-database-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="kedan-label text-slate-500">Administrasi</div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Backup Database</h1>
          <p className="text-slate-500 text-sm mt-1">
            Buat backup penuh database dan restore kapan saja.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="kedan-btn-secondary" data-testid="refresh-backups">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={createBackup}
            disabled={creating}
            className="kedan-btn-primary"
            data-testid="create-backup-btn"
          >
            {creating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Membuat Backup...</>
            ) : (
              <><HardDrive className="w-4 h-4" /> Buat Backup Baru</>
            )}
          </button>
        </div>
      </header>

      {/* Status Message */}
      {message && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium animate-[fadeIn_0.2s_ease-out] ${
            message.type === "success"
              ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
          data-testid="backup-message"
        >
          {message.type === "success" ? (
            <CheckCircle2 className="w-4 h-4 shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 shrink-0" />
          )}
          {message.text}
        </div>
      )}

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-amber-800">Perhatian</div>
          <div className="text-xs text-amber-700 mt-0.5">
            Restore database akan <strong>menimpa seluruh data</strong> dengan data dari file backup.
            Pastikan Anda membuat backup terbaru sebelum melakukan restore.
          </div>
        </div>
      </div>

      {/* Backups Table */}
      <Section title="Daftar Backup" subtitle={`${backups.length} file backup tersedia`}>
        <div className="kedan-card overflow-hidden">
          {loading ? (
            <div className="p-6 space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
          ) : backups.length === 0 ? (
            <EmptyState
              title="Belum ada backup"
              description='Klik "Buat Backup Baru" untuk membuat backup pertama.'
              icon={HardDrive}
            />
          ) : (
            <table className="kedan-table">
              <thead>
                <tr>
                  <th>Nama File</th>
                  <th>Ukuran</th>
                  <th>Tanggal</th>
                  <th className="text-right">Aksi</th>
                </tr>
              </thead>
              <tbody data-testid="backups-table-body">
                {backups.map((b) => (
                  <tr key={b.filename}>
                    <td>
                      <div className="flex items-center gap-2">
                        <HardDrive className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="font-mono text-sm text-slate-900">{b.filename}</span>
                      </div>
                    </td>
                    <td className="font-mono text-sm text-slate-600">{formatSize(b.size)}</td>
                    <td className="text-sm text-slate-500">{formatDate(b.created_at)}</td>
                    <td className="text-right">
                      <div className="inline-flex gap-1.5">
                        <button
                          onClick={() => downloadBackup(b.filename)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
                          data-testid={`download-${b.filename}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </button>
                        <button
                          onClick={() => restoreBackup(b.filename)}
                          disabled={restoring === b.filename}
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            restoring === b.filename
                              ? "bg-slate-100 text-slate-400 cursor-wait"
                              : "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
                          }`}
                          data-testid={`restore-${b.filename}`}
                        >
                          {restoring === b.filename ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Restoring...</>
                          ) : (
                            <><Upload className="w-3.5 h-3.5" /> Restore</>
                          )}
                        </button>
                        <button
                          onClick={() => deleteBackup(b.filename)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors"
                          data-testid={`delete-${b.filename}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Section>
    </div>
  );
}
